<template>
    <div>
        <h4 style="font-weight: 500;" v-text="`Tytuł: ${title}`" />

        <money-input
            v-if="form.value !== undefined"
            required
            text-align="left"
            label="Wartość obrotu"
            tooltip="Deklarowana wartość obrotu z dokumentów zakupowych u dostawcy"
            :price="+form.value"
            @onChange="form.value = $event"
        />

        <the-text-area
            required
            label="Opis (maks. 500 znaków)"
            :value="form.description"
            :maxlength="500"
            @input="form.description = $event"
        />

        <div class="d-flex justify-content-start align-items-center gap-1">
            <input
                type="file"
                ref="fileInput"
                style="display: none"
                multiple
                :accept="EXTENSIONS.join(', ')"
                @change="handleFileChange"
            />

            <the-btn
                class="btn btn-outline-custom"
                text="Wybierz pliki"
                :disabled="form.files.length >= MAX_FILES"
                @click="openFileDialog"
            />

            dostępne formaty: png, jpg, pdf, msg; max. 5 załączników o łącznej wadze do {{ MAX_WEIGHT }} MB
        </div>

        <ul v-if="form.files.length" class="modal-reclamation__file-list">
            <li v-for="(file, index) in form.files" :key="index">
                {{ file.name }} ({{ file.size | filesize }})
                <button
                    class="modal-reclamation__delete text-danger"
                    v-text="'×'"
                    @click="removeFile(index)"
                />
            </li>
        </ul>

        <div v-else v-text="'Brak załączników'" class="text-primary" />

        <div class="text-xs">
            <span class="text-danger">*</span> pola obowiązkowe
        </div>

        <div v-for="error in errors" :key="error.id" class="text-danger">
            {{ error.message }}
        </div>
    </div>
</template>

<script>
import reclamationMixin from './reclamation.mixin.js'

const FORMATS = ['image/png', 'image/jpeg', 'application/pdf', 'application/vnd.ms-outlook']
const EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf', '.msg']
const MAX_FILES = 5
const MAX_WEIGHT = 25 // MB
const ERROR_TIMEOUT = 3000

export default {
    mixins: [reclamationMixin],

    data() {
        return {
            errorMessage: '',
            errors: []
        }
    },

    props: {
        form: {
            type: Object,
            required: true
        }
    },

    methods: {
        openFileDialog() {
            this.$refs.fileInput.click()
        },

        async handleFileChange(event) {
            const files = Array.from(event.target.files)
            const newFiles = []
            this.errors = [] // Reset errors

            // Helper to add error with timeout
            const addError = (message) => {
                const id = Date.now() + Math.random() // Unikalne ID błędu
                this.errors.push({ id, message })
                setTimeout(() => {
                    this.errors = this.errors.filter(error => error.id !== id)
                }, ERROR_TIMEOUT)
            }

            // Calculate total weight of existing and new files
            const totalWeight = this.form.files.reduce((acc, file) => acc + file.size, 0) + files.reduce((acc, file) => acc + file.size, 0)

            if (totalWeight > MAX_WEIGHT * 1024 * 1024) {
                addError(`Maksymalna waga załączników to ${MAX_WEIGHT} MB.`)
                event.target.value = '' // Reset input value
                return
            }

            // Check if files are valid
            for (const file of files) {
                if (this.form.files.length + newFiles.length >= MAX_FILES) {
                    addError(`Możesz dodać maksymalnie ${MAX_FILES} plików.`)
                    break
                }

                if (!FORMATS.includes(file.type)) {
                    addError(`Niedozwolony format pliku: ${file.name}. Dostępne formaty to: png, jpg, pdf, msg.`)
                    continue
                }

                // Process file to get fileData in Base64 format
                const fileData = await this.convertToFileData(file)
                newFiles.push({
                    name: file.name,
                    size: file.size,
                    mimeType: file.type,
                    fileData
                })
            }

            // Add new files to form
            if (newFiles.length > 0) {
                this.form.files = [...this.form.files, ...newFiles]
            }

            // Reset input value
            event.target.value = ''
        },

        // Helper method to convert file to Base64
        convertToFileData(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => {
                    const base64Data = reader.result.split(',')[1] // Remove the `data:*/*;base64,` prefix
                    resolve(base64Data)
                }
                reader.onerror = reject
            })
        },

        removeFile(index) {
            this.form.files.splice(index, 1)
        }
    },

    created() {
        this.EXTENSIONS = EXTENSIONS
        this.MAX_FILES = MAX_FILES
        this.MAX_WEIGHT = MAX_WEIGHT
    }
}
</script>